import axios from 'axios';

import { KanzhongBackend } from 'constant/KanzhongBackend';
import { ServerError, createApiError } from 'stores/ApiClientError';

// --- 型定義 ---

export const ProjectStatus = {
  PREPARING: '準備中',
  ACTIVE: '稼働中',
  CLOSED: '終了',
} as const;

// eslint-disable-next-line @typescript-eslint/no-redeclare
export type ProjectStatus = keyof typeof ProjectStatus;

export type CreateProjectParams = {
  projectName: string;
  projectNameKana: string;
  managerId: string;
  clientId: string;
  projectScaleId?: string;
  distributionId?: string;
  industryIds?: string[];
  techStackIds?: string[];
  startDate?: string;
  remarks?: string;
};

export type UpdateProjectParams = {
  projectId: string;
  projectName: string;
  projectNameKana: string;
  status: ProjectStatus;
  projectScaleId?: string;
  distributionId?: string;
  industryIds?: string[];
  techStackIds?: string[];
  startDate?: string;
  endDate?: string;
  remarks?: string;
};

export type GetProjectParams = {
  projectId: string;
};

export type ListProjectParams = {
  projectName?: string;
  projectNameKana?: string;
  clientId?: string;
  managerId?: string;
  status?: ProjectStatus;
};

export type ProjectListResult = {
  projectId: string;
  projectName: string;
  projectNameKana: string;
  status: ProjectStatus;
  managerId: string;
  managerName: string;
  managerMailAddress: string;
  clientId: string;
  clientName: string;
  projectScaleId?: string;
  distributionId?: string;
  startDate?: string;
  endDate?: string;
  createdAt: string;
  createdBy: string;
  createdByName: string;
  updatedAt: string;
  updatedBy: string;
  updatedByName: string;
};

export type ProjectManagerResult = {
  userId: string;
  mailAddress: string;
  userName: string;
};

export type ProjectClientResult = {
  clientId: string;
  clientName: string;
  clientNameKana: string;
  clientTypeId: string;
  corporateNumber?: string;
  invoiceNumber?: string;
  address?: string;
  url?: string;
  contactName?: string;
  phoneNumber?: string;
  mailAddress?: string;
  contractStartDate: string;
  contractEndDate?: string;
  remarks?: string;
  createdAt: string;
  createdBy: string;
};

export type ProjectIndustryResult = {
  industryId: string;
  industryName: string;
};

export type ProjectTechStackResult = {
  techStackId: string;
  techStackName: string;
  categoryType: string;
  categoryCode: string;
  categoryLabel: string;
};

export type ProjectDetailResult = {
  projectId: string;
  projectName: string;
  projectNameKana: string;
  status: ProjectStatus;
  projectScaleId?: string;
  distributionId?: string;
  startDate?: string;
  endDate?: string;
  remarks?: string;
  createdAt: string;
  createdBy: string;
  createdByName: string;
  updatedAt: string;
  updatedBy: string;
  updatedByName: string;
  manager: ProjectManagerResult;
  client: ProjectClientResult;
  industries?: ProjectIndustryResult[];
  techStacks?: ProjectTechStackResult[];
};

export type ManagerResult = {
  userId: string;
  mailAddress: string;
  userName: string;
};

export type TechStackItem = {
  techStackId: string;
  techStackName: string;
  categoryCode: string;
  categoryLabel: string;
};

export type TechStackResult = {
  TECH_LAYER: TechStackItem[];
  TECH_TYPE: TechStackItem[];
};

// --- API関数 ---

/**
 * プロジェクト登録API
 * @param params.projectName - プロジェクト名（必須, 最大300文字）
 * @param params.projectNameKana - プロジェクト名カナ（必須, 最大300文字）
 * @param params.managerId - マネージャーID（必須, 最大300文字）
 * @param params.clientId - 取引先ID（必須, 最大64文字）
 * @param params.projectScaleId - 規模ID（任意, 最大300文字）
 * @param params.distributionId - 商流ID（任意, 最大300文字）
 * @param params.industryIds - 業界IDリスト（任意）
 * @param params.techStackIds - 技術スタックIDリスト（任意）
 * @param params.startDate - 開始日（任意, format: yyyy-MM-dd'T'HH:mm:ss）
 * @param params.remarks - 備考（任意, 最大10000文字）
 */
export const createProject = async (params: CreateProjectParams): Promise<string> => {
  const sessionId = localStorage.getItem('session-id');

  try {
    const response = await axios.post(KanzhongBackend.CREATE_PROJECT, params, {
      headers: {
        'X-Session-Id': sessionId,
      },
    });

    return response.data.projectId;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const { status, data } = error.response;
      throw createApiError(status, data?.message || error.message);
    }
    throw new ServerError();
  }
};

/**
 * プロジェクト更新API
 * @param params.projectId - プロジェクトID（必須, 最大64文字）
 * @param params.projectName - プロジェクト名（必須, 最大300文字）
 * @param params.projectNameKana - プロジェクト名カナ（必須, 最大300文字）
 * @param params.status - ステータス（必須, ProjectStatus Enum）
 * @param params.projectScaleId - 規模ID（任意, 最大300文字）
 * @param params.distributionId - 商流ID（任意, 最大300文字）
 * @param params.industryIds - 業界IDリスト（任意）
 * @param params.techStackIds - 技術スタックIDリスト（任意）
 * @param params.startDate - 開始日（任意, format: yyyy-MM-dd'T'HH:mm:ss）
 * @param params.endDate - 終了日（任意, format: yyyy-MM-dd'T'HH:mm:ss）
 * @param params.remarks - 備考（任意, 最大10000文字）
 */
export const updateProject = async (params: UpdateProjectParams): Promise<void> => {
  const sessionId = localStorage.getItem('session-id');

  try {
    await axios.post(KanzhongBackend.UPDATE_PROJECT, params, {
      headers: {
        'X-Session-Id': sessionId,
      },
    });
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const { status, data } = error.response;
      throw createApiError(status, data?.message || error.message);
    }
    throw new ServerError();
  }
};

/**
 * プロジェクト取得API
 * @param params.projectId - プロジェクトID（必須, 最大64文字）
 */
export const getProject = async (params: GetProjectParams): Promise<ProjectDetailResult> => {
  const sessionId = localStorage.getItem('session-id');

  try {
    const response = await axios.post(KanzhongBackend.GET_PROJECT, params, {
      headers: {
        'X-Session-Id': sessionId,
      },
    });

    return response.data.result;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const { status, data } = error.response;
      throw createApiError(status, data?.message || error.message);
    }
    throw new ServerError();
  }
};

/**
 * プロジェクト一覧取得API
 * @param params.projectName - プロジェクト名（任意, 前方一致）
 * @param params.projectNameKana - プロジェクト名カナ（任意, 前方一致）
 * @param params.clientId - 取引先ID（任意, 完全一致）
 * @param params.managerId - マネージャーID（任意, 完全一致）
 * @param params.status - ステータス（任意, 完全一致）
 */
export const listProject = async (params: ListProjectParams): Promise<ProjectListResult[]> => {
  const sessionId = localStorage.getItem('session-id');

  try {
    const response = await axios.post(KanzhongBackend.LIST_PROJECT, params, {
      headers: {
        'X-Session-Id': sessionId,
      },
    });

    return response.data.results;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const { status, data } = error.response;
      throw createApiError(status, data?.message || error.message);
    }
    throw new ServerError();
  }
};

/**
 * 課長一覧取得API
 * MANAGER権限を持つユーザーの一覧を取得する
 */
export const listManager = async (): Promise<ManagerResult[]> => {
  const sessionId = localStorage.getItem('session-id');

  try {
    const response = await axios.get(KanzhongBackend.LIST_MANAGER, {
      headers: {
        'X-Session-Id': sessionId,
      },
    });

    return response.data.results;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const { status, data } = error.response;
      throw createApiError(status, data?.message || error.message);
    }
    throw new ServerError();
  }
};

/**
 * 技術スタック取得API
 * 全技術スタックを分類種別ごとにグループ化して取得する
 */
export const getTechStack = async (): Promise<TechStackResult> => {
  const sessionId = localStorage.getItem('session-id');

  try {
    const response = await axios.get(KanzhongBackend.GET_TECH_STACK, {
      headers: {
        'X-Session-Id': sessionId,
      },
    });

    return response.data.result;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const { status, data } = error.response;
      throw createApiError(status, data?.message || error.message);
    }
    throw new ServerError();
  }
};
